import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Trophy, Medal, Award, Crown } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeaderboardPage = () => {
  const { getAuthHeaders } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  // Iter 67 · default "annual" — evita que la landing muestre "sin datos"
  // cuando las ventas del período mensual actual todavía son 0.
  const [period, setPeriod] = useState('annual');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, [period]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/leaderboard?period=${period}`, {
        headers: getAuthHeaders()
      });
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  const getPeriodLabel = () => {
    switch(period) {
      case 'weekly': return 'Esta Semana';
      case 'monthly': return 'Este Mes';
      case 'annual': return 'Este Año';
      default: return '';
    }
  };

  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="leaderboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Clasificación
          </h1>
          <p className="text-slate-500 mt-1">Ranking de gestores - {getPeriodLabel()}</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="leaderboard-period-selector">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="weekly">Semanal</SelectItem>
            <SelectItem value="monthly">Mensual</SelectItem>
            <SelectItem value="annual">Anual</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Podium */}
      {top3.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Second Place */}
          {top3[1] && (
            <Card className="podium-silver border-2 card-hover order-1 md:order-1" data-testid="podium-2">
              <CardContent className="p-6 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-200 mb-4">
                  <Medal className="w-8 h-8 text-slate-500" />
                </div>
                <div className="w-20 h-20 mx-auto rounded-full bg-slate-100 border-4 border-slate-300 flex items-center justify-center text-2xl font-bold text-slate-600 mb-3">
                  {top3[1].gestor_name.charAt(0)}
                </div>
                <h3 className="text-lg font-bold text-slate-900">{top3[1].gestor_name}</h3>
                <p className="text-2xl font-bold text-slate-700 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {formatCurrency(top3[1].total_sales)}
                </p>
                <p className="text-sm text-slate-500 mt-1">{top3[1].total_tickets} pasajes</p>
                <div className="mt-4 inline-block bg-slate-200 px-4 py-1 rounded-full text-sm font-medium text-slate-600">
                  2° Lugar
                </div>
              </CardContent>
            </Card>
          )}

          {/* First Place */}
          {top3[0] && (
            <Card className="podium-gold border-2 card-hover order-0 md:order-2 md:-mt-4" data-testid="podium-1">
              <CardContent className="p-6 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 mb-4">
                  <Crown className="w-8 h-8 text-yellow-600" />
                </div>
                <div className="w-24 h-24 mx-auto rounded-full bg-yellow-50 border-4 border-yellow-400 flex items-center justify-center text-3xl font-bold text-yellow-700 mb-3">
                  {top3[0].gestor_name.charAt(0)}
                </div>
                <h3 className="text-xl font-bold text-slate-900">{top3[0].gestor_name}</h3>
                <p className="text-3xl font-bold text-yellow-600 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {formatCurrency(top3[0].total_sales)}
                </p>
                <p className="text-sm text-slate-500 mt-1">{top3[0].total_tickets} pasajes</p>
                <div className="mt-4 inline-block bg-yellow-400 px-4 py-1 rounded-full text-sm font-bold text-yellow-900">
                  <Trophy className="w-4 h-4 inline mr-1" />
                  1° Lugar
                </div>
              </CardContent>
            </Card>
          )}

          {/* Third Place */}
          {top3[2] && (
            <Card className="podium-bronze border-2 card-hover order-2 md:order-3" data-testid="podium-3">
              <CardContent className="p-6 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
                  <Award className="w-8 h-8 text-amber-600" />
                </div>
                <div className="w-20 h-20 mx-auto rounded-full bg-amber-50 border-4 border-amber-600 flex items-center justify-center text-2xl font-bold text-amber-700 mb-3">
                  {top3[2].gestor_name.charAt(0)}
                </div>
                <h3 className="text-lg font-bold text-slate-900">{top3[2].gestor_name}</h3>
                <p className="text-2xl font-bold text-amber-700 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {formatCurrency(top3[2].total_sales)}
                </p>
                <p className="text-sm text-slate-500 mt-1">{top3[2].total_tickets} pasajes</p>
                <div className="mt-4 inline-block bg-amber-200 px-4 py-1 rounded-full text-sm font-medium text-amber-800">
                  3° Lugar
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Rest of the table */}
      <Card className="shadow-sm" data-testid="leaderboard-table">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">
            Tabla de Clasificación Completa
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {leaderboard.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              No hay datos de ventas para este período
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-20">Posición</TableHead>
                  <TableHead>Gestor</TableHead>
                  <TableHead className="text-right">Ventas</TableHead>
                  <TableHead className="text-right">Pasajes</TableHead>
                  <TableHead className="text-right">N° Ventas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaderboard.map((entry) => (
                  <TableRow key={entry.gestor_id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        entry.rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                        entry.rank === 2 ? 'bg-slate-300 text-slate-700' :
                        entry.rank === 3 ? 'bg-amber-500 text-white' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {entry.rank}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-semibold text-blue-600">
                          {entry.gestor_name.charAt(0)}
                        </div>
                        <span className="font-medium text-slate-900">{entry.gestor_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-slate-900">
                      {formatCurrency(entry.total_sales)}
                    </TableCell>
                    <TableCell className="text-right text-slate-600">
                      {entry.total_tickets}
                    </TableCell>
                    <TableCell className="text-right text-slate-600">
                      {entry.sales_count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaderboardPage;
